import { cn } from '@/lib/utils'

export interface StackedSegment {
  key: string
  label: string
  value: number
  /** A fill class: `bg-chart-1`, `bg-chart-1/55` … */
  fill: string
}

/** One bar split into shares, with a 2px gap between the parts. Parts with
 *  no value are left out; the whole reads out as "미국 $3.20, 한국 $1.10 …"
 *  (or `label`). */
export function StackedBar({
  segments,
  height = 8,
  className,
  label,
  format = String,
}: {
  segments: StackedSegment[]
  height?: number
  className?: string
  label?: string
  /** How a part's value reads in its title and in the bar's name. */
  format?: (v: number) => string
}) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0)
  const shown = segments.filter((s) => s.value > 0)
  const name = label ?? segments.map((s) => `${s.label} ${format(s.value)}`).join(', ')
  return (
    <div role="img" aria-label={name} style={{ height }} className={cn('flex w-full min-w-0 gap-[2px] overflow-hidden rounded-full', total === 0 && 'bg-muted', className)}>
      {total > 0 &&
        shown.map((s) => (
          <span key={s.key} title={`${s.label} ${format(s.value)}`} className={cn('h-full min-w-[2px]', s.fill)} style={{ flexGrow: s.value, flexBasis: 0 }} />
        ))}
    </div>
  )
}

/** The chart slots in order, then the rest (plan 1-3: 국가 상위 5 + 기타).
 *  Literal, so Tailwind sees every class. */
export const SHARE_FILLS = ['bg-chart-1', 'bg-chart-2', 'bg-chart-3', 'bg-chart-4', 'bg-chart-5'] as const
export const REST_FILL = 'bg-muted-foreground/30'

/** The biggest `top` parts in the chart slots, largest first, and everything
 *  else as one "기타" part. Parts at or below zero are dropped. */
export function shareSegments(items: { key: string; label: string; value: number }[], top = 5): StackedSegment[] {
  const sorted = items.filter((i) => i.value > 0).sort((a, b) => b.value - a.value)
  const head = sorted.slice(0, Math.min(top, SHARE_FILLS.length))
  const rest = sorted.slice(head.length).reduce((s, i) => s + i.value, 0)
  const out: StackedSegment[] = head.map((i, n) => ({ ...i, fill: SHARE_FILLS[n] }))
  if (rest > 0) out.push({ key: '__rest', label: '기타', value: rest, fill: REST_FILL })
  return out
}
